import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  FocusTrap,
  Text,
  PinInput,
  Button,
  Group,
  Stack,
  Center,
  ThemeIcon,
  Box,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck, IconPhoneCheck } from "@tabler/icons-react";
import { DataContext } from "../../context/DataContext";
import { isMobile } from "react-device-detect";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../utils/edge";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { useMediaQuery } from "@mantine/hooks";
import useAdjustInputSize from "../../hooks/useAdjustInputSize";
import FloatingLabelInput from "../../components/floating-label-input";
import DateOfBirthSelect from "../../components/date-of-birth-select";
import AddressInput from "../../components/address-input";
import UkAddressInput from "../../components/uk-address-input";
export default function PhoneMatch() {
  const { data, dispatch, text, routeSequence, currentIndex, navigateToNext, settings } = useContext(DataContext);
  const theme = useMantineTheme();

  // Edge mode detection
  const isEdgeMode = data?.edge === true;
  const [isVerifying, setIsVerifying] = useState(false);
  console.log('[PhoneMatch Page] Edge mode check:', { isEdgeMode, dataEdge: data?.edge });

  const [loading, setLoading] = useState(true);
  const [isOtpInput, setIsOtpInput] = useState(false);
  const [template, setTemplate] = useState({});
  const [result, setResult] = useState(false);
  const [hasHandledFailure, setHasHandledFailure] = useState(false);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);
  const smallScreen = useMediaQuery("(max-width: 40em)");
  const isIframe = window !== window.top;
  const inputSize = useAdjustInputSize();
  const firstNameInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const collectDob = true;
  const [collectAddress, setCollectAddress] = useState(false);
  const [collectSsn, setCollectSsn] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const currentPath = "phonematch";

  const hasAdditionalFields = collectAddress || collectSsn;

  useEffect(() => {
    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath
    );
    const inputs = matchedRoute?.inputs || [];
    const isUKAddressSetting =
      settings?.journey?.settings?.isUKAddress ||
      settings?.journey?.settings?.isUkAddress ||
      matchedRoute?.settings?.isUKAddress ||
      matchedRoute?.settings?.isUkAddress;
    // Only collect address if explicitly in inputs array
    const addressNeeded = inputs.includes("address") || inputs.includes("fulladdress") || inputs.includes("residence");
    const ssnNeeded = inputs.includes("ssn") || inputs.includes("last4ssn") || inputs.includes("lastfourssn") || inputs.includes("ssn4");
    setCollectAddress(addressNeeded);
    setCollectSsn(ssnNeeded);
    setDebugInfo({
      isUKAddressSetting,
      inputs,
      addressNeeded,
      ssnNeeded,
      journeySettings: settings?.journey?.settings,
      matchedRoute,
    });
  }, [settings, currentPath]);

  // Check if this component is skippable based on route settings
  const currentRoute = routeSequence[currentIndex];
  const isSkippable = currentRoute?.skipable === true;

  // Determine which fields should be disabled from route configuration
  const disabledFields = Array.isArray(currentRoute?.component?.disabled)
    ? currentRoute.component.disabled
    : Array.isArray(currentRoute?.disabled)
    ? currentRoute.disabled
    : [];

  const getNewPin = () => {
    setIsOtpInput(false);
    setTemplate({});
    setResult(false);
    form.setFieldValue("pin", "");
  };

  // Handler for skip button
  const handleSkip = () => {
    // Update session state to mark phonematch as complete
    const updatedSessionState = {
      ...data.sessionState,
      phonematch: "COMPLETED"
    };

    // Update the session state
    dispatch({
      type: "SET_SESSION_STATE",
      payload: updatedSessionState
    });

    // Mark current component as complete to allow navigation
    dispatch({
      type: "SET_IS_CURRENT_COMPONENT_COMPLETE",
      payload: true
    });

    // Trigger navigation to the next step
    dispatch({ type: "TRIGGER_NAVIGATION" });
  };

  useStatusCheck(!isEdgeMode && result, false, "phonematch");
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling);

  const form = useForm({
    initialValues: {
      fn: data.fn || "",
      ln: data.ln || "",
      phone: data.phone || "",
      dob: data.dob || "",
      addr: data.addr || "",
      city: data.city || "",
      state: data.state || "",
      zip: data.zip || "",
      ssn: data.ssn || "",
      pin: "",
    },
    validate: (values) => {
      if (isOtpInput) {
        return {
          pin: values.pin.length < 6 ? text.phonematch.code.error : null,
        };
      }
      const errors = {};

      if (
        !disabledFields.includes("phone") &&
        values.phone.replace(/\D/g, "").length !== 10
      ) {
        errors.phone = text.general.phoneError;
      }

      if (!disabledFields.includes("fn") && values.fn.length < 2) {
        errors.fn = text.general.firstNameError;
      }

      if (!disabledFields.includes("ln") && values.ln.length < 2) {
        errors.ln = text.general.lastNameError;
      }

      if (collectDob && activeStep === 0 && !disabledFields.includes("dob")) {
        const dobPattern =
          /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
        const isDobFormatCorrect = dobPattern.test(values.dob);
        let dobError = text.general.dateOfBirthError;

        if (isDobFormatCorrect) {
          const year = parseInt(values.dob.substring(0, 4), 10);
          const month = parseInt(values.dob.substring(4, 6), 10) - 1;
          const day = parseInt(values.dob.substring(6, 8), 10);
          const date = new Date(year, month, day);
          if (
            date.getFullYear() === year &&
            date.getMonth() === month &&
            date.getDate() === day
          ) {
            dobError = null;
          }
        }
        errors.dob = dobError;
      }

      if (collectAddress && activeStep === 1 && !disabledFields.includes("addr")) {
        if (
          !values.addr ||
          !values.city ||
          !values.state ||
          values.zip.trim().length !== 5
        ) {
          errors.addr = "Address is required";
        } else {
          errors.addr = null;
        }
      }

      if (collectSsn && activeStep === 1 && !disabledFields.includes("ssn")) {
        if (/[^0-9]/.test(values.ssn) || values.ssn.length !== 4) {
          errors.ssn = text.general.lastFourSSNError;
        } else {
          errors.ssn = null;
        }
      }

      return errors;
    },
  });

  const handleDobChange = (dob) => {
    form.setFieldValue("dob", dob.year + dob.month + dob.day);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  const isUKAddressSetting = debugInfo.isUKAddressSetting;
  const AddressComponent = isUKAddressSetting
    ? UkAddressInput
    : AddressInput;

  const handleNext = () => {
    const validation = form.validate();
    if (
      !validation.errors.fn &&
      !validation.errors.ln &&
      !validation.errors.phone &&
      (!collectDob || !validation.errors.dob)
    ) {
      setActiveStep(1);
      form.clearErrors();
    }
  };

  // Edge mode: poll retrieve endpoint for result after initial submit
  const pollForResult = async (requestId) => {
    const maxAttempts = 100; // ~5 minutes
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            request_id: requestId,
            type: "phonematch",
          }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[PhoneMatch Page] Retrieve poll result:', result);

          // Check for final status (PASS/FAIL) — no OTP needed
          if (result.action === "PASS" || result.action === "FAIL") {
            if (result.action !== "PENDING") {
              setEdgeStatusPolling(true);
              return; // Stop polling, edge status check handles the rest
            }
          }

          // Check if OTP is needed (PENDING)
          if (result.action === "PENDING") {
            // Build the continuation template from the retrieve response
            const continueTemplate = result.continuation_template || {
              codematch: {
                key: "",
                confirmation: result.confirmation,
                token: result.token,
              }
            };
            setTemplate(continueTemplate);
            setIsOtpInput(true);
            setLoading(false);
            return; // Stop polling, show OTP input
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("PhoneMatch polling timeout: No result received");
          setLoading(false);
        }
      } catch (error) {
        console.error("PhoneMatch polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setLoading(false);
        }
      }
    };

    poll();
  };

  const submitHandler = () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    {
      setLoading(true);
      setHasHandledFailure(false); // Reset failure handling state on new request
      if (!disabledFields.includes("phone")) {
        data.phone = form.values.phone;
      }
      if (!disabledFields.includes("fn")) {
        data.fn = form.values.fn;
      }
      if (!disabledFields.includes("ln")) {
        data.ln = form.values.ln;
      }
      if (collectDob && !disabledFields.includes("dob")) {
        data.dob = form.values.dob;
      }
      if (collectAddress && !disabledFields.includes("addr")) {
        data.addr = form.values.addr;
        data.city = form.values.city;
        data.state = form.values.state;
        data.zip = form.values.zip;
      }
      if (collectSsn && !disabledFields.includes("ssn")) {
        data.ssn = form.values.ssn;
      }
      data.isOtpInput = isOtpInput;
      if (isOtpInput) {
        template.codematch.key = form.values.pin;
        data.phonematch_continue = template;
      }

      // Edge mode OTP submission goes to the queue publisher
      if (isEdgeMode && isOtpInput) {
        const reqId = settings.request_id;
        console.log('[PhoneMatch Page] Submitting OTP to publisher:', { reqId, code: form.values.pin });

        const otpData = { ...data, queue: "phonematch" };

        fetch(getEdgeQueueUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(otpData),
        })
          .then((res) => res.json())
          .then((result) => {
            console.log('[PhoneMatch Page] OTP publisher result:', result);
            // After OTP is queued, start edge status polling for final PASS/FAIL
            setEdgeStatusPolling(true);
          })
          .catch((error) => {
            console.error("PhoneMatch OTP submission error:", error);
            setLoading(false);
          });
        return;
      }

      // Determine URL and request data based on edge mode
      const apiUrl = isEdgeMode
        ? getEdgeQueueUrl()
        : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/phonematch";

      const requestData = isEdgeMode
        ? { ...data, queue: "phonematch" }
        : data;

      console.log('[PhoneMatch Page] Submitting to:', apiUrl, { isEdgeMode });

      fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (responseData) {
          if (isEdgeMode) {
            // Edge mode: queue accepted, now poll retrieve for the result
            const reqId = responseData.request_id || settings.request_id;
            if (responseData.request_id) {
              dispatch({ type: "SET_SETTINGS", payload: { ...settings, request_id: responseData.request_id } });
            }

            // Initial submit, poll for result (may return OTP template or direct result)
            if (reqId) {
              pollForResult(reqId);
            } else {
              console.error("No request_id found in edge response");
              setLoading(false);
            }
            return;
          }

          // Non-edge mode: handle synchronous response
          if (responseData.result.action === "PENDING" && !isOtpInput) {
            setLoading(false);
            setTemplate(responseData.continuations.outofband.template);
            setIsOtpInput(true);
          } else if (responseData.result.action === "PASS") {
            setLoading(true);
            setResult(true);
          } else {
            // For failed results, keep loading state true to prevent form from showing
            if (!hasHandledFailure) {
              setResult(true);
              setHasHandledFailure(true);
              dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            }
          }
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
          setIsVerifying(false);
        });
    }
  };

  useEffect(() => {
    setLoading(false);
    form.clearErrors();
  }, [hasAdditionalFields]);

  if (loading) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          {!isOtpInput && (
            <>
              <Stack spacing={8} mt={inputSize === "md" ? 5 : 5}>
                <Center>
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 60 : 55}
                    radius={"xl"}
                  >
                    <IconPhoneCheck
                      size={inputSize === "md" ? 40 : 35}
                      stroke={1.5}
                      color="teal"
                    />
                  </ThemeIcon>
                </Center>

                <Stack spacing={0} mt="sm">
                  <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                    {text.phonematch.title}
                  </Text>

                  <Text c="dimmed" fz={inputSize}>
                    {text.phonematch.subTitle}
                  </Text>
                </Stack>
              </Stack>

              {hasAdditionalFields ? (
                <Stack mt={20} spacing={"md"}>
                  {activeStep === 0 && (
                    <>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.firstName}
                          placeholder={text.general.firstNamePlaceholder}
                          {...form.getInputProps("fn")}
                          disabled={disabledFields.includes("fn")}
                        />
                      </Box>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.lastName}
                          {...form.getInputProps("ln")}
                          disabled={disabledFields.includes("ln")}
                        />
                      </Box>
                      {collectDob && (
                        <Box>
                          <DateOfBirthSelect
                            isSinglePageForm={true}
                            onDobChange={handleDobChange}
                            {...form.getInputProps("dob")}
                            disabled={disabledFields.includes("dob")}
                          />
                        </Box>
                      )}
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.phone}
                          tooltip={text.general.phoneTooltip}
                          placeholder={text.general.phonePlaceholder}
                          inputMode="tel"
                          {...form.getInputProps("phone")}
                          disabled={disabledFields.includes("phone")}
                          customOnChange={(event) => {
                            let value = event.target.value || "";
                            value = value.replace(/\D/g, "").substring(0, 10);
                            value = value.replace(
                              /(\d{3})(\d{3})(\d{4})/,
                              "$1-$2-$3"
                            );
                            form.getInputProps("phone").onChange(value);
                            form.setFieldValue("phone", value);
                          }}
                        />
                      </Box>
                    </>
                  )}
                  {activeStep === 1 && (
                    <>
                      {collectAddress && (
                        <Box>
                          <AddressComponent
                            onAddressChange={handleAddressChange}
                            value={{
                              addr: form.values.addr,
                              city: form.values.city,
                              state: form.values.state,
                              zip: form.values.zip,
                            }}
                            error={form.errors.addr}
                            {...form.getInputProps("addr")}
                          />
                        </Box>
                      )}
                      {collectSsn && (
                        <Box>
                          <FloatingLabelInput
                            size={"lg"}
                            label={text.general.lastFourSSN}
                            isMaskedInput
                            maxLength={4}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            {...form.getInputProps("ssn")}
                            disabled={disabledFields.includes("ssn")}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              ) : (
                <Stack mt={20} spacing={"md"}>
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.firstName}
                      placeholder={text.general.firstNamePlaceholder}
                      {...form.getInputProps("fn")}
                      disabled={disabledFields.includes("fn")}
                    />
                  </Box>
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.lastName}
                      {...form.getInputProps("ln")}
                      disabled={disabledFields.includes("ln")}
                    />
                  </Box>
                  {collectDob && (
                    <Box>
                      <DateOfBirthSelect
                        isSinglePageForm={true}
                        onDobChange={handleDobChange}
                        {...form.getInputProps("dob")}
                        disabled={disabledFields.includes("dob")}
                      />
                    </Box>
                  )}
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.phone}
                      tooltip={text.general.phoneTooltip}
                      placeholder={text.general.phonePlaceholder}
                      inputMode="tel"
                      {...form.getInputProps("phone")}
                      disabled={disabledFields.includes("phone")}
                      customOnChange={(event) => {
                        let value = event.target.value || "";
                        value = value.replace(/\D/g, "").substring(0, 10);
                        value = value.replace(
                          /(\d{3})(\d{3})(\d{4})/,
                          "$1-$2-$3"
                        );
                        form.getInputProps("phone").onChange(value);
                        form.setFieldValue("phone", value);
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </>
          )}
          {isOtpInput && (
            <>
              <Center mt={inputSize === "md" ? 25 : 10}>
                <Stack align="center">
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 70 : 65}
                    radius={"xl"}
                  >
                    <IconShieldCheck
                      stroke={1}
                      size={inputSize === "md" ? 50 : 45}
                      color="teal"
                    />
                  </ThemeIcon>

                  <Text fz={inputSize === "md" ? 30 : 23} fw={700}>
                    {text.phonematch.code.title}
                  </Text>
                  <Text
                    ta="center"
                    c="dimmed"
                    fz={inputSize === "md" ? 24 : 14}
                  >
                    {text.phonematch.code.subTitle}
                  </Text>
                </Stack>
              </Center>

              <Group position="center" mt={40}>
                <FocusTrap>
                  <PinInput
                    data-autofocus
                    type="number"
                    oneTimeCode
                    size={"xl"}
                    length={6}
                    {...form.getInputProps("pin")}
                    onComplete={() => {
                      submitHandler();
                    }}
                  />
                </FocusTrap>
              </Group>

            </>
          )}
        </Box>
        <Box pb="sm">
          <Stack spacing={0}>
            {isOtpInput ? (
              <Stack align="center" spacing={0} mt="xl">
                <Text fz="xs" c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[6]}>
                  {text.phonematch.code.newCodeNote}
                </Text>
                <Button
                  onClick={getNewPin}
                  variant="subtle"
                  styles={{
                    root: {
                      color: theme.colorScheme === "dark" ? theme.colors.gray[3] : theme.colors.dark[6],
                    },
                  }}
                >
                  {text.phonematch.code.newCodeButton}
                </Button>
              </Stack>
            ) : hasAdditionalFields ? (
              activeStep === 0 ? (
                <Button mt="xl" fullWidth size={"lg"} onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button mt="xl" fullWidth type="submit" size={"lg"}>
                  {text.phonematch.button}
                </Button>
              )
            ) : (
              <Button mt="xl" fullWidth type="submit" size={"lg"}>
                {text.phonematch.button}
              </Button>
            )}

            {hasAdditionalFields && !isOtpInput && activeStep === 1 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={() => setActiveStep(0)}
                size="xs"
                compact
                mt={8}
              >
                Back
              </Button>
            )}

            {isSkippable && !isOtpInput && activeStep === 0 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={handleSkip}
                size="xs"
                compact
                mt={8}
              >
                {currentRoute?.skipableText || "Skip this step"}
              </Button>
            )}
          </Stack>
        </Box>
      </form>
    </>
  );
}
